/**
 * DID Routing API client for Kamailio database management.
 *
 * @see interstellar/services/kamailio_service/endpoints.py
 */

import interstellarApi from './interstellarApi';

export interface DidRouting {
  id: number;
  did: string;
  freeswitch_ip: string;
  freeswitch_port: number;
  account_id: number | null;
  carrier: string | null;
  created_at: string;
  updated_at: string;
}

export interface DidRoutingListResponse {
  total_count: number;
  page: number;
  page_size: number;
  routes: DidRouting[];
}

export interface CreateDidRoutingPayload {
  did: string;
  freeswitch_ip: string;
  freeswitch_port?: number;
  account_id?: number | null;
  carrier?: string | null;
}

export interface UpdateDidRoutingPayload {
  freeswitch_ip?: string;
  freeswitch_port?: number;
  account_id?: number | null;
  carrier?: string | null;
}

export interface CreateDidRoutingResponse {
  message: string;
  id: number;
}

export interface UpdateDidRoutingResponse {
  message: string;
  id: number;
}

export interface DeleteDidRoutingResponse {
  message: string;
  id: number;
}

/**
 * Fetch paginated list of DID routing entries.
 */
export const fetchDidRoutingList = async (
  page = 1,
  pageSize = 50,
  q?: string,
  accountId?: number
): Promise<DidRoutingListResponse> => {
  const params: Record<string, string | number> = { page, page_size: pageSize };
  if (q) params.q = q;
  if (accountId !== undefined) params.account_id = accountId;

  const response = await interstellarApi.get<DidRoutingListResponse>('/kamailio/did-routing', {
    params,
  });
  return response.data;
};

/**
 * Get a single DID routing entry by ID.
 */
export const fetchDidRouting = async (routeId: number): Promise<DidRouting> => {
  const response = await interstellarApi.get<DidRouting>(`/kamailio/did-routing/${routeId}`);
  return response.data;
};

/**
 * Create a new DID routing entry.
 */
export const createDidRouting = async (
  payload: CreateDidRoutingPayload
): Promise<CreateDidRoutingResponse> => {
  const response = await interstellarApi.post<CreateDidRoutingResponse>(
    '/kamailio/did-routing',
    payload
  );
  return response.data;
};

/**
 * Update an existing DID routing entry.
 */
export const updateDidRouting = async (
  routeId: number,
  payload: UpdateDidRoutingPayload
): Promise<UpdateDidRoutingResponse> => {
  const response = await interstellarApi.put<UpdateDidRoutingResponse>(
    `/kamailio/did-routing/${routeId}`,
    payload
  );
  return response.data;
};

/**
 * Delete a DID routing entry.
 */
export const deleteDidRouting = async (routeId: number): Promise<DeleteDidRoutingResponse> => {
  const response = await interstellarApi.delete<DeleteDidRoutingResponse>(
    `/kamailio/did-routing/${routeId}`
  );
  return response.data;
};
